/**
 * JWT claim reading for the client's own expiry decisions. The token is
 * decoded, never verified: verification belongs to the API Gateway JWT
 * authorizer, and a client that trusted its own verification would be
 * trusting a value it also holds. The app only needs to know whether a
 * stored token is still worth sending.
 *
 * Nothing here logs. A decoded payload contains the operator's identity
 * claims, so it is returned to the caller and never printed.
 */

import type { AuthUser } from './types';

/** Treat a token as expired this long before its `exp`, so a request that
 * is in flight when the clock crosses `exp` is not the one that discovers
 * it. Cognito ID tokens live an hour by default, so a minute is cheap. */
export const EXPIRY_SKEW_MS = 60_000;

interface JwtClaims {
  exp?: number;
  sub?: string;
  email?: string;
  'custom:client_id'?: string;
  'custom:role'?: string;
}

function decodeBase64Url(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  return atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
}

/** Reads the payload of a JWT. Returns null for anything that is not a
 * decodable three-part token, so a corrupted storage value is handled as a
 * missing session rather than a crash on launch. */
export function decodeJwtClaims(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const parsed: unknown = JSON.parse(decodeBase64Url(parts[1]));
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Epoch milliseconds of the token's `exp`, or null when the token cannot
 * be decoded or carries no `exp`. */
export function readTokenExpiry(token: string): number | null {
  const claims = decodeJwtClaims(token) as JwtClaims | null;
  return typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
}

/** A token with no readable expiry counts as expired: an undecodable
 * credential cannot be sent with any confidence, and the refresh path
 * handles the recovery. */
export function isTokenExpired(token: string, nowMs: number = Date.now(), skewMs: number = EXPIRY_SKEW_MS): boolean {
  const expiresAt = readTokenExpiry(token);
  if (expiresAt === null) return true;
  return nowMs >= expiresAt - skewMs;
}

/** Maps ID-token claims onto the operator identity the app shows. The tenant
 * claim is `custom:client_id`, the only tenant-bearing claim the Cognito pool
 * issues and the one `apps/capture-api/src/shared/auth.ts` reads. */
export function userFromClaims(claims: Record<string, unknown>): AuthUser {
  const typed = claims as JwtClaims;
  return {
    sub: typeof typed.sub === 'string' ? typed.sub : '',
    email: typeof typed.email === 'string' ? typed.email : '',
    tenantId: typeof typed['custom:client_id'] === 'string' ? typed['custom:client_id'] : null,
    role: typeof typed['custom:role'] === 'string' ? typed['custom:role'] : null,
  };
}
